import { languages, getTranslation } from './translations';
import React, { useState, useEffect } from 'react';
import { Activity, Target, Layers, Heart, ChevronRight, MapPin, Clock, Phone, MessageCircle, Star, Award, Menu, X, Facebook, Instagram } from 'lucide-react';

export default function TSMCWebsite() {
  const [activeService, setActiveService] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // 自動偵測瀏覽器語言
  const [currentLang, setCurrentLang] = useState(() => {
    // 1. 先檢查用戶之前的選擇
    const savedLang = localStorage.getItem('preferredLang');
    if (savedLang && ['zh', 'en', 'fil', 'vi', 'id', 'th'].includes(savedLang)) {
      return savedLang;
    }
    
    // 2. 否則偵測瀏覽器語言
    const browserLang = (navigator.language || navigator.userLanguage || 'zh').toLowerCase();
    
    if (browserLang.startsWith('zh')) return 'zh';
    if (browserLang.startsWith('en')) return 'en';
    if (browserLang.startsWith('fil') || browserLang.startsWith('tl')) return 'fil';
    if (browserLang.startsWith('vi')) return 'vi';
    if (browserLang.startsWith('id')) return 'id';
    if (browserLang.startsWith('th')) return 'th';
    
    // 3. 預設中文
    return 'zh';
  });
  
  const t = getTranslation(currentLang);
  const [currentReview, setCurrentReview] = useState(0);
  
  // 切換語言並記住選擇
  const handleLangChange = (lang) => {
    setCurrentLang(lang);
    localStorage.setItem('preferredLang', lang);
  };


  // 國際進修照片數據
  const trainingPhotos = [
    {
      year: '2023',
      title: '完成專業課程',
      description: '跟隨日本講師學習進階技術',
      image: 'training_6.jpg',
      type: 'certificate'
    },
    {
      year: '2022',
      title: '國際講師交流',
      description: '與來自加拿大的專業講師深入交流',
      image: 'training_5.jpg',
      type: 'exchange'
    },
    {
      year: '2021',
      title: '課後深度交流',
      description: '在講師家中討論技術與理念',
      image: 'training_4.jpg',
      type: 'lifestyle'
    },
    {
      year: '2021',
      title: '生活中的學習',
      description: '與法國講師一起衝浪，在生活中交流',
      image: 'training_3.jpg',
      type: 'lifestyle'
    },
    {
      year: '2020',
      title: '診所實習交流',
      description: '跟隨美國講師在診所實地學習',
      image: 'training_2.jpg',
      type: 'clinical'
    },
    {
      year: '2019',
      title: '國際研討會',
      description: '參與國際專業研討會',
      image: 'training_1.jpg',
      type: 'conference'
    }
  ];

  // 客戶評論數據
  const reviews = [
    {
      name: 'Chen Vicky',
      rating: 5,
      date: '2025/01',
      content: '推👍因為脊椎問題跟頭痛的問題過來，經過講解跟調理後，改善很多，會固定回來保養👍👍👍',
      lang: 'zh'
    },
    {
      name: 'JH Li',
      rating: 5,
      date: '2024/12',
      content: 'Brian很細心詢問身體狀況，第一次調理長短腳就有明顯的改善，走路平衡很多。搭配白板畫圖協助理解，調理以外，知識獲益良多。是定期保養身體的好地方！',
      lang: 'zh'
    },
    {
      name: 'Jonelyn Velasco',
      rating: 5,
      date: '2024/09',
      content: "I've experienced the best chiropractic ever in my life, a nice and friendly one. He treated me well. I'm from the Philippines. 🥰😊 Godbless 🥰",
      lang: 'en'
    },
    {
      name: 'Huang Ant',
      rating: 5,
      date: '2024/02',
      content: '環境好，技術一流，有效改善背部痠痛麻的問題👍',
      lang: 'zh'
    },
    {
      name: 'Ii Li',
      rating: 5,
      date: '2024/01',
      content: '老闆技術非常的專業厲害，一下就找到問題點，講解也用模型和app讓你能馬上秒懂。',
      lang: 'zh'
    },
    {
      name: '鍾阿桃',
      rating: 5,
      date: '2023/01',
      content: '本身有駝背的狀況，師傅用心找出問題，經過幾次調整，改善非常多，同時肩頸緊繃及腰痠問題也有獲得改善，現在固定每兩週給師傅調整，非常推薦！',
      lang: 'zh'
    },
    {
      name: 'Show He',
      rating: 5,
      date: '2022/07',
      content: '經常性頭痛，背部某處長期痠痛，看過幾間復健科都沒什麼改善，朋友介紹我去找師傅調整看看，初次體驗之後頭痛及背部痛點改善了，才知道是頸椎、胸椎歪斜導致的症狀，真的很神奇！！！',
      lang: 'zh'
    },
    {
      name: 'Serena Lu',
      rating: 5,
      date: '2022/07',
      content: '之前一直都有轉頭會痛的（轉左邊）的問題，深深困擾我一個多月，跑去復健科診所數次也做過復健，沒有改善，後來到老師這邊喬完後到現在脖子轉頭完全沒有問題，太神奇了！',
      lang: 'zh'
    }
  ];





  // 平滑滾動到指定區塊
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // 導航列高度
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  // 加入結構化資料到 head
  useEffect(() => {
    // ============================================
    // Google Analytics 4 追蹤代碼
    // ============================================
    const gaScript1 = document.createElement('script');
    gaScript1.async = true;
    gaScript1.src = 'https://www.googletagmanager.com/gtag/js?id=G-T3W99VQ5ET';
    document.head.appendChild(gaScript1);

    const gaScript2 = document.createElement('script');
    gaScript2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-T3W99VQ5ET');
    `;
    document.head.appendChild(gaScript2);

    // Google Search Console 驗證（帳號 1）
    const metaGoogle1 = document.createElement('meta');
    metaGoogle1.name = 'google-site-verification';
    metaGoogle1.content = 'tKTZ0JTmc0q75Qr9nIB2AJeJPZfz6TxdmBo3FgHhYh0';
    document.head.appendChild(metaGoogle1);

    // Google Search Console 驗證（帳號 2）
    const metaGoogle2 = document.createElement('meta');
    metaGoogle2.name = 'google-site-verification';
    metaGoogle2.content = 'VhdpOBvQtnhrBRRkDmOyyKCjenaFvemjNG_eYogle0M';
    document.head.appendChild(metaGoogle2);

    // SEO Meta Tags
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = '台筋殿 TSMC - 台南佳里美式整復推拿與運動按摩。服務久坐辦公族、久站工作族、體力工作族及規律運動族，提供非醫療性的日常身體調理、深層筋膜放鬆與運動後保養。';
    document.head.appendChild(metaDescription);

    const metaKeywords = document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = '台南整復,佳里整復,美式整復,台南推拿,佳里推拿,運動按摩,深層按摩,筋膜放鬆,台南按摩,佳里按摩,久坐辦公族,久站工作族,體力工作族,規律運動族,運動後放鬆,日常身體調理,身體保養';
    document.head.appendChild(metaKeywords);

    // Open Graph Tags (社群分享)
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = '台筋殿 TSMC - 台南佳里美式整復推拿 | 運動按摩 | 日常身體調理';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = '服務久坐辦公族、久站工作族、體力工作族與規律運動族，提供非醫療性的美式整復推拿、運動按摩及日常身體保養。';
    document.head.appendChild(ogDescription);

    const ogImage = document.createElement('meta');
    ogImage.setAttribute('property', 'og:image');
    ogImage.content = 'https://tsmchiro.com/og-image.jpg';
    document.head.appendChild(ogImage);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = 'https://tsmchiro.com';
    document.head.appendChild(ogUrl);

    // Schema.org 結構化資料 - LocalBusiness
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "台筋殿",
      "alternateName": ["TSMC", "Technology of Stretching & Mobility Chiro"],
      "description": "台南佳里美式整復推拿與運動按摩服務，適合久坐辦公族、久站工作族、體力工作族與規律運動族安排非醫療性的日常身體調理。",
      "image": "https://tsmchiro.com/logo.jpg",
      "priceRange": "$$",
      "@id": "https://tsmchiro.com",
      "url": "https://tsmchiro.com",
      "telephone": "+886-919-909-002",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "文新里安西130-95號",
        "addressLocality": "佳里區",
        "addressRegion": "台南市",
        "postalCode": "722",
        "addressCountry": "TW"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 23.1628,
        "longitude": 120.1775
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday"],
          "opens": "11:00",
          "closes": "21:40"
        }
      ],
      "sameAs": [
        "https://www.facebook.com/tsmchiro",
        "https://www.instagram.com/tsmchiro",
        "https://lin.ee/ZCMyfyR"
      ]
    });
    document.head.appendChild(script);

    // Schema.org 結構化資料 - Service
    const serviceSchema = document.createElement('script');
    serviceSchema.type = 'application/ld+json';
    serviceSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "美式整復與運動按摩",
      "provider": {
        "@type": "LocalBusiness",
        "name": "台筋殿 TSMC",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "佳里區",
          "addressRegion": "台南市"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "台南市"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "整復與按摩服務",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "美式整復 Chiropractic",
              "description": "依個人舒適度安排特定手法與日常身體保養"
            },
            "price": "1000",
            "priceCurrency": "TWD"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "美式整復加運動按摩",
              "description": "特定手法搭配深層筋膜放鬆與運動後日常保養"
            },
            "price": "1500",
            "priceCurrency": "TWD"
          }
        ]
      }
    });
    document.head.appendChild(serviceSchema);

    // Schema.org 結構化資料 - FAQ
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "美式整復和一般推拿有什麼不同？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "在部分 Chiropractic 流派的理論與實務中，會以特定手法關注關節活動與整體身體使用方式；一般推拿則多著重肌肉放鬆。台筋殿依個人舒適度與日常需求安排非醫療性的調理流程。"
          }
        },
        {
          "@type": "Question",
          "name": "整復會痛嗎？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "專業的整復過程中可能會有些微不適感，但不應該感到劇烈疼痛。我們會根據每個人的耐受度調整手法力道，確保在安全且舒適的範圍內進行調理。"
          }
        },
        {
          "@type": "Question",
          "name": "需要多久做一次整復？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "頻率依個人生活型態、放鬆需求與偏好而異，可自行安排單次體驗或日常保養；本服務不提供醫療療程建議。"
          }
        },
        {
          "@type": "Question",
          "name": "台筋殿的服務區域？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "台筋殿位於台南市佳里區，主要服務台南地區客戶，包括佳里、麻豆、學甲、將軍、七股、西港、安定等鄰近區域。歡迎線上預約或 LINE 諮詢。"
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    document.title = "台筋殿 TSMC - 台南佳里美式整復推拿 | 運動按摩 | 日常身體調理";
    
    return () => {
      // 清理 Google Analytics 腳本
      document.head.removeChild(gaScript1);
      document.head.removeChild(gaScript2);
      // 清理 Google Search Console 驗證碼
      document.head.removeChild(metaGoogle1);
      document.head.removeChild(metaGoogle2);
      // 清理其他元素
      document.head.removeChild(metaDescription);
      document.head.removeChild(metaKeywords);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogImage);
      document.head.removeChild(ogUrl);
      document.head.removeChild(script);
      document.head.removeChild(serviceSchema);
      document.head.removeChild(faqSchema);
    };
  }, []);

  // 原版精修測試：區塊與流程列捲動淡入（每項只播放一次）
  useEffect(() => {
    const sections = document.querySelectorAll('.refined-site section:not(#home)');
    const processRows = document.querySelectorAll('.refined-site #process .grid > .group');
    const revealTargets = [...sections, ...processRows];

    if (!('IntersectionObserver' in window)) {
      revealTargets.forEach((target) => target.classList.add('refined-in'));
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('refined-in');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -8% 0px'
    });

    revealTargets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  // 評論輪播自動播放
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length);
    }, 5000); // 每 5 秒切換一次

    return () => clearInterval(timer);
  }, [reviews.length]);

  const navItems = [
    { id: 'home', label: t.navHome },
    { id: 'method', label: t.navMethod },
    { id: 'services', label: t.navServices },
    { id: 'about', label: t.navAbout },
    { id: 'faq', label: t.navFaq },
    { id: 'contact', label: t.navContact },
    { id: 'blog', label: t.navBlog, isExternal: true, url: '/blog/' }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 refined-site">
      {/* 浮動按鈕群組 */}
      <div className="fixed right-6 bottom-6 z-50 flex flex-col gap-3 refined-floating-actions">
        {/* LINE 按鈕 */}
        <a
          href="https://lin.ee/ZCMyfyR"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group refined-float-line"
          aria-label="LINE 諮詢"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320" className="w-8 h-8">
            <path fill="#fff" d="M266.66,144.92c0-47.74-47.86-86.58-106.69-86.58S53.28,97.18,53.28,144.92c0,42.8,38,78.65,89.22,85.42,3.48.75,8.21,2.29,9.4,5.26,1.08,2.7.71,6.93.35,9.65,0,0-1.25,7.53-1.52,9.13-.47,2.7-2.15,10.55,9.24,5.76s61.44-36.18,83.82-61.95h0C259.25,181.24,266.66,164,266.66,144.92Z"/>
          </svg>
        </a>

        {/* 預約按鈕 */}
        <a
          href="/booking/"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group refined-float-book"
          aria-label="立即預約"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-zinc-950">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </a>

        {/* Facebook 按鈕 */}
        <a
          href="https://www.facebook.com/tsmchiro"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-[#1877f2] hover:bg-[#0d65d9] rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group"
          aria-label="Facebook 粉絲專頁"
        >
          <Facebook className="w-7 h-7 text-white" />
        </a>

        {/* Instagram 按鈕 */}
        <a
          href="https://www.instagram.com/tsmchiro"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group"
          aria-label="Instagram 帳號"
        >
          <Instagram className="w-7 h-7 text-white" />
        </a>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={() => scrollToSection('home')}
              className="flex items-center space-x-3 cursor-pointer group"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                <img 
                  src="/logo.jpg" 
                  alt="台筋殿 TSMC LOGO" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <span className="text-xl block refined-wordmark-d">台筋殿</span>
                <span className="text-xs text-zinc-400 hidden sm:block">台南佳里整復推拿</span>
              </div>
            </button>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-zinc-300 hover:text-amber-400 transition-colors text-sm font-medium"
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="px-4 py-2 text-zinc-300 hover:text-amber-400 transition-colors text-sm font-medium"
                  >
                    {item.label}
                  </button>
                )
              ))}
            </div>

            <div className="flex items-center space-x-2">
              {/* Language Selector - 下拉式選單 */}
              <div className="relative">
                <select
                  value={currentLang}
                  onChange={(e) => handleLangChange(e.target.value)}
                  className="appearance-none flex items-center space-x-2 px-3 sm:px-4 py-2 pr-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-amber-500/50 transition-all duration-300 text-sm font-medium text-zinc-100 cursor-pointer focus:outline-none focus:border-amber-500"
                >
                  <option value="zh">🇹🇼 中文</option>
                  <option value="en">🇺🇸 English</option>
                  <option value="fil">🇵🇭 Filipino</option>
                  <option value="vi">🇻🇳 Tiếng Việt</option>
                  <option value="id">🇮🇩 Bahasa</option>
                  <option value="th">🇹🇭 ไทย</option>
                </select>
                <ChevronRight className="w-4 h-4 text-amber-400 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
              </div>

              <a 
                href="/booking/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hidden sm:inline-block px-4 sm:px-6 py-2 bg-gradient-to-r from-amber-500 to-teal-500 text-zinc-950 font-semibold rounded-lg hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 text-sm"
                aria-label="立即預約"
              >
                {t.bookNow}
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-zinc-400 hover:text-zinc-100"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-zinc-800">
              {navItems.map((item) => (
                item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-left px-4 py-3 text-zinc-300 hover:bg-zinc-800 hover:text-amber-400 transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-3 text-zinc-300 hover:bg-zinc-800 hover:text-amber-400 transition-colors"
                  >
                    {item.label}
                  </button>
                )
              ))}
              
          {/* 手機版語言切換 - 下拉式選單 */}
          <div className="px-4 py-3 mt-2">
            <select
              value={currentLang}
              onChange={(e) => handleLangChange(e.target.value)}
              className="w-full appearance-none px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 hover:text-amber-400 transition-colors cursor-pointer focus:outline-none focus:border-amber-500"
            >
              <option value="zh">🇹🇼 中文 Chinese</option>
              <option value="en">🇺🇸 English</option>
              <option value="fil">🇵🇭 Filipino</option>
              <option value="vi">🇻🇳 Tiếng Việt</option>
              <option value="id">🇮🇩 Bahasa Indonesia</option>
              <option value="th">🇹🇭 ไทย</option>
            </select>
          </div>
              
              <a 
                href="/booking/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block mx-4 mt-4 px-6 py-3 bg-gradient-to-r from-amber-500 to-teal-500 text-zinc-950 font-semibold rounded-lg text-center"
              >
                {t.bookNow}
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 to-zinc-950"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          {/* SEO 優化的 H1 - 包含主要關鍵字 */}
          <h1 className="sr-only">台南佳里專業美式整復推拿 | 運動按摩 | 深層筋膜放鬆 - 台筋殿 TSMC</h1>
          
          {/* 視覺呈現的主標題 */}
          <div className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight" role="heading" aria-level="1">
            <span className="bg-gradient-to-r from-amber-400 via-amber-300 to-teal-400 bg-clip-text text-transparent">
              {t.heroTitle1}
            </span>
            <br />
            <span className="text-zinc-100">{t.heroTitle2}</span>
          </div>
          
          <p className="text-lg sm:text-xl lg:text-2xl text-zinc-400 mb-3 sm:mb-4 max-w-3xl mx-auto">
            {t.heroSubtitle}
          </p>
          
          <p className="text-base sm:text-lg text-zinc-500 mb-8 sm:mb-12 max-w-2xl mx-auto">
            {t.heroDesc}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <span className="hero-heartbeat-shell">
              <a
                href="/booking/"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-500 to-teal-500 text-zinc-950 text-base sm:text-lg font-bold rounded-xl hover:shadow-2xl hover:shadow-amber-500/30 transition-all duration-300 transform hover:scale-105 inline-block w-full sm:w-auto text-center"
              >
                {t.bookNowFull}
                <ChevronRight className="inline-block ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </span>
            
            <a 
              href="https://lin.ee/ZCMyfyR" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="px-6 sm:px-8 py-3 sm:py-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-base sm:text-lg font-semibold rounded-xl border border-zinc-600 transition-all duration-300 inline-flex items-center justify-center w-full sm:w-auto"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {t.lineConsult}
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm text-zinc-400">
            <div className="flex items-center">
              <Award className="w-4 h-4 text-amber-400 mr-2" />
              <span>{t.heroCert}</span>
            </div>
            <div className="flex items-center">
              <Star className="w-4 h-4 text-amber-400 mr-2" />
              <span>{t.heroPrice}</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 text-amber-400 mr-2" />
              <span>{t.heroTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* What is Chiropractic Section */}
      <section id="what" className="py-16 sm:py-20 px-4 bg-zinc-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            {t.whatIsTitle}<span className="bg-gradient-to-r from-amber-400 to-teal-400 bg-clip-text text-transparent">{t.whatIsChiro}</span>？
          </h2>
          <p className="text-center text-zinc-400 mb-12 text-base sm:text-lg">{t.whatIsSubtitle}</p>
          
          <div className="relative overflow-hidden bg-zinc-800/90 rounded-[1.75rem] p-6 sm:p-9 lg:p-12 border border-zinc-700/90 mb-8 shadow-2xl shadow-black/20">
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" />
            <div className="relative prose prose-lg prose-invert max-w-none">
              <p className="text-zinc-300 text-base sm:text-lg leading-8 sm:leading-9 mb-7">
                {t.whatIsPara1}<span className="text-amber-400 font-semibold">{t.whatIsChiroFull}</span>{t.whatIsPara1b}<span className="text-teal-400 font-semibold">{t.whatIsFounder}</span>{t.whatIsPara1c}
              </p>

              {t.whatIsRegulation && (
                <p className="text-zinc-300 text-base sm:text-lg leading-8 sm:leading-9 mb-7">
                  {t.whatIsRegulation}
                </p>
              )}

              <p className="text-zinc-300 text-base sm:text-lg leading-8 sm:leading-9 mb-7">
                {t.whatIsPara2}<span className="text-amber-400 font-semibold">{t.whatIsRestore}</span>{t.whatIsPara2b}
              </p>

              <div className="bg-gradient-to-br from-amber-500/[0.10] via-zinc-900 to-teal-500/[0.07] rounded-2xl sm:rounded-3xl border border-amber-400/30 overflow-hidden mt-9">
                <div className="px-5 sm:px-7 py-5 sm:py-6 border-b border-amber-400/20">
                  <h3 className="text-lg sm:text-xl font-semibold text-zinc-100 tracking-wide"><span className="text-amber-400 mr-2">✦</span>{t.whatIsBoxTitle}</h3>
                </div>
                <p className="text-zinc-300 text-sm sm:text-base leading-7 px-5 sm:px-7 pt-5 mb-1">
                  {t.whatIsBoxDesc}
                </p>
                <div className="px-5 sm:px-7 pb-3 sm:pb-4">
                  {[t.whatIsCycle1, t.whatIsCycle2, t.whatIsCycle3, t.whatIsCycle4].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4 py-4 sm:py-5 border-b border-zinc-700/70 last:border-b-0">
                      <span className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-zinc-950 font-bold flex items-center justify-center shadow-lg shadow-amber-500/10">{idx + 1}</span>
                      <p className="text-zinc-300 text-sm sm:text-base leading-7 pt-1.5">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {t.whatIsDisclaimer && (
                <p className="mt-6 mb-0 text-xs sm:text-sm leading-6 text-zinc-500">
                  {t.whatIsDisclaimer}
                </p>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-zinc-950" />
              </div>
              <h3 className="text-xl font-bold text-zinc-100 mb-3">{t.chiroCardTitle}</h3>
              <p className="text-zinc-400 text-sm">{t.chiroCardDesc}<span className="text-amber-400 font-semibold">{t.chiroCardFocus}</span>{t.chiroCardDesc2}</p>
            </div>

            <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                <Activity className="w-8 h-8 text-zinc-950" />
              </div>
              <h3 className="text-xl font-bold text-zinc-100 mb-3">{t.massageCardTitle}</h3>
              <p className="text-zinc-400 text-sm">{t.massageCardDesc}<span className="text-teal-400 font-semibold">{t.massageCardFocus}</span>{t.massageCardDesc2}</p>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-amber-500/10 to-teal-500/10 border border-amber-500/20 rounded-2xl p-6 text-center">
            <p className="text-base sm:text-lg text-zinc-300">
              <span className="font-bold text-amber-400">{t.whatIsSummary}</span>{t.whatIsSummary2}
            </p>
          </div>
        </div>
      </section>

      {/* TSMC Methodology */}
      <section id="method" className="py-16 sm:py-20 px-4 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-amber-400 to-teal-400 bg-clip-text text-transparent">{t.methodTitle}</span> {t.methodTitleSuffix}
          </h2>
          <p className="text-center text-zinc-400 mb-12 sm:mb-16 text-base sm:text-lg">{t.methodSubtitle}</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              {
                letter: 'T',
                title: t.methodT,
                subtitle: t.methodTSub,
                desc: t.methodTDesc,
                icon: Target,
                gradient: 'from-amber-500 to-amber-600'
              },
              {
                letter: 'S',
                title: t.methodS,
                subtitle: t.methodSSub,
                desc: t.methodSDesc,
                icon: Layers,
                gradient: 'from-teal-500 to-teal-600'
              },
              {
                letter: 'M',
                title: t.methodM,
                subtitle: t.methodMSub,
                desc: t.methodMDesc,
                icon: Activity,
                gradient: 'from-amber-500 to-teal-500'
              },
              {
                letter: 'C',
                title: t.methodC,
                subtitle: t.methodCSub,
                desc: t.methodCDesc,
                icon: Heart,
                gradient: 'from-teal-500 to-amber-500'
              }
            ].map((item, idx) => (
              <article key={idx} className="group relative bg-zinc-800 rounded-2xl p-6 border border-zinc-700 hover:border-zinc-600 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/10">
                <div className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className="w-8 h-8 text-zinc-950" />
                </div>
                <div className="text-5xl font-bold text-zinc-700 mb-2">{item.letter}</div>
                <h3 className="text-xl font-bold text-zinc-100 mb-1">{item.title}</h3>
                <p className="text-amber-400 font-semibold mb-2">{item.subtitle}</p>
                <p className="text-zinc-400 text-sm">{item.desc}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 bg-zinc-900 rounded-2xl p-6 sm:p-8 border border-zinc-700">
            <h3 className="text-xl sm:text-2xl font-bold text-center text-zinc-100 mb-6">{t.methodSystemTitle}</h3>
            <div className="text-zinc-300 text-sm sm:text-base leading-relaxed space-y-4">
              <p className="text-center">
                <span className="text-amber-400 font-semibold">{t.methodSystem1}</span>
              </p>
              <p className="text-center">
                {t.methodSystem2}
              </p>
              <p className="text-center text-zinc-400">
                {t.methodSystem3}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-20 px-4 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">{t.services}</h2>
          <p className="text-center text-zinc-400 mb-12 sm:mb-16 text-base sm:text-lg">{t.servicesDesc}</p>
          
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-12">
            {/* Chiropractic Card */}
            <article 
              className="relative bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-3xl p-6 sm:p-8 border border-zinc-700 hover:border-amber-500/50 transition-all duration-300 cursor-pointer group"
              onMouseEnter={() => setActiveService('chiro')}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => window.open('/booking/', '_blank')}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500 rounded-full filter blur-3xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-10 h-10 text-zinc-950" />
                </div>
                
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 text-zinc-100">{t.chiro}</h3>
                <p className="text-lg sm:text-xl text-amber-400 mb-2">Chiropractic</p>
                <p className="text-sm text-zinc-500 mb-4">{t.chiroService}</p>
                
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl sm:text-5xl font-bold text-zinc-100">$1000</span>
                  <span className="text-zinc-400 ml-3">{t.chiroPriceSuffix}</span>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                    <span className="text-zinc-300 ml-2">{t.chiroFeature1}</span>
                  </div>
                  <div className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                    <span className="text-zinc-300 ml-2">{t.chiroFeature2}</span>
                  </div>
                  <div className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                    <span className="text-zinc-300 ml-2">{t.chiroFeature3}</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-zinc-700">
                  <p className="text-sm text-zinc-400">
                    {t.suitable}<span className="text-amber-400 font-semibold">{t.chiroSuitable}</span>
                  </p>
                </div>
              </div>
            </article>

            {/* Sports Massage Card */}
            <article 
              className="relative bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-3xl p-6 sm:p-8 border border-zinc-700 hover:border-teal-500/50 transition-all duration-300 cursor-pointer group"
              onMouseEnter={() => setActiveService('massage')}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => window.open('/booking/', '_blank')}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500 rounded-full filter blur-3xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
                  <Activity className="w-10 h-10 text-zinc-950" />
                </div>
                
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 text-zinc-100">{t.massage}</h3>
                <p className="text-lg sm:text-xl text-teal-400 mb-2">Chiropractic + Sports Massage</p>
                <p className="text-sm text-zinc-500 mb-4">{t.massageService}</p>
                
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl sm:text-5xl font-bold text-zinc-100">$2300</span>
                  <span className="text-zinc-400 ml-3">{t.massagePriceSuffix}</span>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span className="text-zinc-300 ml-2">{t.massageFeature1}</span>
                  </div>
                  <div className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span className="text-zinc-300 ml-2">{t.massageFeature2}</span>
                  </div>
                  <div className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-teal-400 mt-1 flex-shrink-0" />
                    <span className="text-zinc-300 ml-2">{t.massageFeature3}</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-zinc-700">
                  <p className="text-sm text-zinc-400">
                    {t.suitable}<span className="text-teal-400 font-semibold">{t.massageSuitable}</span>
                  </p>
                </div>
              </div>
            </article>
          </div>

          <div className="bg-gradient-to-r from-amber-500/10 to-teal-500/10 border border-amber-500/20 rounded-2xl p-6 text-center">
            <p className="text-base sm:text-lg text-zinc-300">
              <span className="font-bold text-amber-400">{t.servicesSummary1}</span>{t.servicesSummary2}
            </p>
          </div>
        </div>
      </section>

      {/* Service Flow Section - 服務流程（照片牆設計）*/}
      <section id="process" className="py-16 sm:py-20 px-4 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          {/* 標題 */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-amber-400 to-teal-400 bg-clip-text text-transparent">
                {t.serviceFlowTitle}
              </span>
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg max-w-3xl mx-auto">
              {t.serviceFlowSubtitle}
            </p>
          </div>

          {/* 照片牆網格 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            
            {/* 步驟卡片數據 */}
            {[
              { num: 1, title: t.step1Title, desc: t.step1Desc, img: '/service-photos/step1.jpg' },
              { num: 2, title: t.step2Title, desc: t.step2Desc, img: '/service-photos/step2.jpg' },
              { num: 3, title: t.step3Title, desc: t.step3Desc, img: '/service-photos/step3.jpg' },
              { num: 4, title: t.step4Title, desc: t.step4Desc, img: '/service-photos/step4.jpg' },
              { num: 5, title: t.step5Title, desc: t.step5Desc, img: '/service-photos/step5.jpg' },
              { num: 6, title: t.step6Title, desc: t.step6Desc, img: '/service-photos/step6.jpg' },
              { num: 7, title: t.step7Title, desc: t.step7Desc, img: '/service-photos/step7.jpg' },
            ].map((step) => (
              <div 
                key={step.num}
                className="group bg-zinc-900 rounded-2xl overflow-hidden border-2 border-zinc-800 hover:border-amber-500 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-amber-500/20"
              >
                {/* 照片區域 */}
                <div className="relative h-64 bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden">
                  <img 
                    src={step.img} 
                    alt={`台南佳里整復 ${step.title} - 台筋殿 TSMC 專業服務示範`}
                    title={step.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  
                  {/* 步驟編號 */}
                  <div className="absolute top-4 left-4 w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                    {step.num}
                  </div>
                </div>

                {/* 卡片內容 */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-amber-400 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
            
          </div>
        </div>
      </section>

      {/* 適合對象 Section */}
      <section id="audience" className="py-16 sm:py-20 px-4 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            {t.whoNeedsTitle}
          </h2>
          <p className="text-center text-zinc-400 mb-12 sm:mb-16 text-base sm:text-lg">{t.whoNeedsSubtitle}</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: t.targetOffice,
                symptoms: [t.targetOfficeSymptom1, t.targetOfficeSymptom2, t.targetOfficeSymptom3, t.targetOfficeSymptom4],
                icon: '💼'
              },
              {
                title: t.targetAthlete,
                symptoms: [t.targetAthleteSymptom1, t.targetAthleteSymptom2, t.targetAthleteSymptom3, t.targetAthleteSymptom4],
                icon: '🏃'
              },
              {
                title: t.targetPosture,
                symptoms: [t.targetPostureSymptom1, t.targetPostureSymptom2, t.targetPostureSymptom3, t.targetPostureSymptom4],
                icon: '🧍'
              },
              {
                title: t.targetChronic,
                symptoms: [t.targetChronicSymptom1, t.targetChronicSymptom2, t.targetChronicSymptom3, t.targetChronicSymptom4],
                icon: '🛠️'
              },
              {
                title: t.targetSenior,
                symptoms: [t.targetSeniorSymptom2, t.targetSeniorSymptom3, t.targetSeniorSymptom4],
                icon: '👴'
              },
              {
                title: t.targetPregnant,
                symptoms: [t.targetPregnantSymptom1, t.targetPregnantSymptom2, t.targetPregnantSymptom4],
                icon: '🌿'
              }
            ].map((group, idx) => (
              <div key={idx} className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700 hover:border-amber-500/30 transition-all duration-300">
                <div className="text-4xl mb-3">{group.icon}</div>
                <h3 className="text-xl font-bold text-zinc-100 mb-4">{group.title}</h3>
                <ul className="space-y-2">
                  {group.symptoms.map((symptom, i) => (
                    <li key={i} className="flex items-center text-zinc-400 text-sm">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-2"></div>
                      {symptom}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 px-4 bg-zinc-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            {t.aboutTitle}
          </h2>
          <p className="text-center text-zinc-400 mb-8 sm:mb-12 text-base sm:text-lg">{t.aboutSubtitle}</p>
          
          <div className="bg-zinc-800 rounded-3xl p-6 sm:p-8 lg:p-12 border border-zinc-700">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-6">
                {t.aboutPara1}<span className="text-amber-400 font-semibold">{t.aboutPara1Highlight}</span>{t.aboutPara1b}
              </p>
              
              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-6">
                {t.aboutPara2}<span className="text-teal-400 font-semibold">{t.aboutPara2Highlight}</span>{t.aboutPara2b}
              </p>
              
              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-6">
                {t.aboutPara3}<span className="text-amber-400 font-semibold">{t.aboutPara3Highlight}</span>{t.aboutPara3b}
              </p>
              
              <div className="bg-zinc-900 rounded-xl p-4 sm:p-6 border-l-4 border-amber-500 mt-8">
                <p className="text-zinc-300 italic text-sm sm:text-base">
                  "{t.aboutQuote}"
                </p>
                <p className="text-zinc-500 text-xs sm:text-sm mt-3">{t.aboutQuoteAuthor}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* International Training Section - 國際進修（拍立得風格）*/}
      <section id="training" className="py-16 sm:py-20 px-4 bg-zinc-900">
        <div className="max-w-6xl mx-auto">
          {/* 標題 */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              {t.trainingTitle}
            </h2>
            <p className="text-amber-400 text-lg sm:text-xl mb-6 font-medium">
              {t.trainingSubtitle}
            </p>
            <p className="text-zinc-300 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
              {t.trainingIntro}
            </p>
          </div>

          {/* 拍立得照片牆 */}
          <div className="relative min-h-[800px] sm:min-h-[600px]">
            {/* 照片 1 - 左上，大 */}
            <div 
              className="absolute top-0 left-0 sm:left-[5%] w-[280px] sm:w-[320px] bg-white p-4 shadow-2xl transform rotate-[-4deg] hover:rotate-[-2deg] hover:scale-105 transition-all duration-300 cursor-pointer z-10"
              style={{ transformOrigin: 'center center' }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img 
                  src="./images/training_1.jpg"
                  alt="國際進修"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-3 text-center font-handwriting text-zinc-700 text-sm sm:text-base">
                國際研討交流
              </div>
            </div>

            {/* 照片 2 - 右上，中 */}
            <div 
              className="absolute top-[40px] right-0 sm:right-[8%] w-[240px] sm:w-[280px] bg-white p-4 shadow-2xl transform rotate-[5deg] hover:rotate-[3deg] hover:scale-105 transition-all duration-300 cursor-pointer z-20"
              style={{ transformOrigin: 'center center' }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img 
                  src="./images/training_2.jpg"
                  alt="國際進修"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-3 text-center font-handwriting text-zinc-700 text-sm">
                專業課程學習
              </div>
            </div>

            {/* 照片 3 - 左中，小 */}
            <div 
              className="absolute top-[320px] sm:top-[220px] left-[20px] sm:left-[15%] w-[200px] sm:w-[220px] bg-white p-3 shadow-2xl transform rotate-[8deg] hover:rotate-[6deg] hover:scale-105 transition-all duration-300 cursor-pointer z-30"
              style={{ transformOrigin: 'center center' }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img 
                  src="./images/training_3.jpg"
                  alt="國際進修"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-2 text-center font-handwriting text-zinc-700 text-xs sm:text-sm">
                課後交流
              </div>
            </div>

            {/* 照片 4 - 中央，大 */}
            <div 
              className="absolute top-[180px] sm:top-[280px] left-[50%] translate-x-[-50%] w-[260px] sm:w-[300px] bg-white p-4 shadow-2xl transform rotate-[-3deg] hover:rotate-[-1deg] hover:scale-105 transition-all duration-300 cursor-pointer z-40"
              style={{ transformOrigin: 'center center' }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img 
                  src="./images/training_4.jpg"
                  alt="國際進修"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-3 text-center font-handwriting text-zinc-700 text-sm sm:text-base">
                深度交流學習
              </div>
            </div>

            {/* 照片 5 - 右下，中 */}
            <div 
              className="absolute top-[520px] sm:top-[480px] right-[10px] sm:right-[10%] w-[240px] sm:w-[260px] bg-white p-4 shadow-2xl transform rotate-[-6deg] hover:rotate-[-4deg] hover:scale-105 transition-all duration-300 cursor-pointer z-50"
              style={{ transformOrigin: 'center center' }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img 
                  src="./images/training_5.jpg"
                  alt="國際進修"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-3 text-center font-handwriting text-zinc-700 text-sm">
                國際講師指導
              </div>
            </div>

            {/* 照片 6 - 左下，中 */}
            <div 
              className="absolute top-[580px] sm:top-[520px] left-[40px] sm:left-[20%] w-[220px] sm:w-[240px] bg-white p-3 shadow-2xl transform rotate-[4deg] hover:rotate-[2deg] hover:scale-105 transition-all duration-300 cursor-pointer z-60"
              style={{ transformOrigin: 'center center' }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img 
                  src="./images/training_6.jpg"
                  alt="國際進修"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-2 text-center font-handwriting text-zinc-700 text-xs sm:text-sm">
                完成專業課程
              </div>
            </div>
          </div>

          {/* 結語 */}
          <div className="mt-24 sm:mt-16 text-center">
            <p className="text-zinc-400 text-lg sm:text-xl italic max-w-2xl mx-auto leading-relaxed">
              「這些年來，走過的每一步，<br />都是為了給您更專業的服務」
            </p>
          </div>

          {/* 統計數據 */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-2xl p-6 text-center backdrop-blur-sm">
              <div className="text-3xl sm:text-4xl font-bold text-amber-400 mb-2">
                {t.trainingStats1Val}
              </div>
              <div className="text-zinc-300 text-sm sm:text-base">
                {t.trainingStats1}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/20 rounded-2xl p-6 text-center backdrop-blur-sm">
              <div className="text-3xl sm:text-4xl font-bold text-teal-400 mb-2">
                {t.trainingStats2Val}
              </div>
              <div className="text-zinc-300 text-sm sm:text-base">
                {t.trainingStats2}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-2xl p-6 text-center backdrop-blur-sm">
              <div className="text-3xl sm:text-4xl font-bold text-blue-400 mb-2">
                {t.trainingStats3Val}
              </div>
              <div className="text-zinc-300 text-sm sm:text-base">
                {t.trainingStats3}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section - 客戶評論 */}
      <section id="reviews" className="py-16 sm:py-20 px-4 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-6xl mx-auto">
          {/* 標題 */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              {t.reviewsTitle}
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg mb-2">
              {t.reviewsSubtitle}
            </p>
            <div className="flex items-center justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
              ))}
              <span className="ml-2 text-amber-500 font-semibold">5.0</span>
            </div>
          </div>

          {/* 評論卡片輪播 */}
          <div className="relative max-w-5xl mx-auto">
            {/* 評論卡片容器 */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentReview * 100}%)` }}
              >
                {reviews.map((review, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <div className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-zinc-700/50 hover:border-amber-500/50 transition-all duration-300 shadow-xl">
                      {/* 評分 */}
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                        ))}
                      </div>

                      {/* 評論內容 */}
                      <p className="text-zinc-200 text-base sm:text-lg leading-relaxed mb-6 min-h-[120px]">
                        「{review.content}」
                      </p>

                      {/* 評論者資訊 */}
                      <div className="flex items-center justify-between pt-4 border-t border-zinc-700">
                        <div>
                          <p className="text-white font-semibold">{review.name}</p>
                          <p className="text-zinc-500 text-sm">{review.date}</p>
                        </div>
                        <div className="text-zinc-500 text-sm">
                          {t.reviewsFrom}: Google
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 左右切換按鈕 */}
            <button
              onClick={() => setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-zinc-800/90 hover:bg-zinc-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 z-10"
              aria-label="Previous review"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            
            <button
              onClick={() => setCurrentReview((prev) => (prev + 1) % reviews.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-zinc-800/90 hover:bg-zinc-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 z-10"
              aria-label="Next review"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* 指示點 */}
            <div className="flex justify-center gap-2 mt-8">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentReview(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentReview 
                      ? 'bg-amber-500 w-8' 
                      : 'bg-zinc-600 hover:bg-zinc-500'
                  }`}
                  aria-label={`Go to review ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* 查看所有評論按鈕 */}
          <div className="text-center mt-12">
            <a
              href="https://maps.app.goo.gl/4qFz4UqvsWxzmd386"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-amber-500/50 hover:scale-105"
            >
              <Star className="w-5 h-5" />
              {t.reviewsViewAll}
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-20 px-4 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">{t.faqTitle}</h2>
          <p className="text-center text-zinc-400 mb-12 sm:mb-16 text-base sm:text-lg">{t.faqSubtitle}</p>
          
          <div className="space-y-4">
            {[
              { q: t.faq1Q, a: t.faq1A },
              { q: t.faq2Q, a: t.faq2A },
              { q: t.faq3Q, a: t.faq3A },
              { q: t.faq4Q, a: t.faq4A },
              { q: t.faq5Q, a: t.faq5A },
              { q: t.faq6Q, a: t.faq6A }
            ].map((faq, idx) => (
              <details key={idx} className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden group">
                <summary className="px-6 py-4 cursor-pointer font-semibold text-zinc-100 hover:bg-zinc-700 transition-colors list-none flex items-center justify-between">
                  <span>{faq.q}</span>
                  <ChevronRight className="w-5 h-5 text-amber-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-4 text-zinc-400 text-sm sm:text-base leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-4 bg-zinc-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 mb-8 sm:mb-12">{t.ctaDesc}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 sm:mb-12">
            <a 
              href="/booking/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="px-8 py-4 bg-gradient-to-r from-amber-500 to-teal-500 text-zinc-950 text-lg font-bold rounded-xl hover:shadow-2xl hover:shadow-amber-500/30 transition-all duration-300 transform hover:scale-105 inline-block"
            >
              {t.ctaBooking}
            </a>
            
            <a 
              href="https://lin.ee/ZCMyfyR" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-lg font-semibold rounded-xl border border-zinc-600 transition-all duration-300 inline-flex items-center justify-center"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {t.ctaLine}
            </a>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 text-sm text-zinc-400">
            <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
              <div className="text-2xl mb-2">💰</div>
              <div className="font-semibold text-zinc-200 mb-1">{t.ctaTransparent}</div>
              <div>{t.ctaTransparentDesc}</div>
            </div>
            <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
              <div className="text-2xl mb-2">🎯</div>
              <div className="font-semibold text-zinc-200 mb-1">{t.ctaCert}</div>
              <div>{t.ctaCertDesc}</div>
            </div>
            <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
              <div className="text-2xl mb-2">⏰</div>
              <div className="font-semibold text-zinc-200 mb-1">{t.ctaFlex}</div>
              <div>{t.ctaFlexDesc}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-zinc-950 border-t border-zinc-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Clock className="w-5 h-5 text-amber-400 mr-2" />
                {t.footerHours}
              </h3>
              <p className="text-zinc-400 mb-2">{t.footerHoursDays}</p>
              <p className="text-zinc-300 font-semibold mb-3">{t.footerHoursTime}</p>
              <p className="text-zinc-500 text-sm">{t.footerHoursClosed}</p>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <MapPin className="w-5 h-5 text-teal-400 mr-2" />
                {t.footerLocation}
              </h3>
              <div className="rounded-2xl border border-teal-500/20 bg-zinc-900/70 p-4 shadow-lg shadow-black/20">
                <p className="text-zinc-400 text-sm mb-1">{t.footerLocationSearch}</p>
                <p className="text-zinc-100 font-semibold leading-relaxed">722 {t.footerLocationCity}</p>
                <p className="text-zinc-300 mb-3 leading-relaxed">{t.footerLocationAddr}</p>
                <p className="text-zinc-500 text-sm leading-relaxed mb-4">{t.footerLocationGuide}</p>
                <a
                  href="https://www.google.com/maps/search/?api=1&amp;query=%E5%8F%B0%E7%AD%8B%E6%AE%BF%20TSMC%20%E7%BE%8E%E6%97%A5%E6%BE%B3%E5%8A%A0%20%E6%95%B4%E5%BE%A9%E5%B7%A5%E4%BD%9C%E5%AE%A4%20722%E5%8F%B0%E5%8D%97%E5%B8%82%E4%BD%B3%E9%87%8C%E5%8D%80%E6%96%87%E6%96%B0%E9%87%8C%E5%AE%89%E8%A5%BF130-95%E8%99%9F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-teal-500 to-amber-400 px-4 py-3 text-sm font-bold text-zinc-950 shadow-lg shadow-teal-500/20 hover:brightness-110 transition-all"
                  aria-label={t.footerLocationOpenMap}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  {t.footerLocationOpenMap}
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Phone className="w-5 h-5 text-amber-400 mr-2" />
                {t.footerContact}
              </h3>
              <a 
                href="tel:0919909002" 
                className="text-zinc-300 hover:text-amber-400 transition-colors block mb-3 text-lg font-semibold"
              >
                0919-909-002
              </a>
              <a 
                href="https://lin.ee/ZCMyfyR" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-zinc-400 hover:text-teal-400 transition-colors flex items-center mb-3"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t.footerContactLine}
              </a>
              
              <div className="pt-4 border-t border-zinc-800 mt-4">
                <p className="text-sm text-zinc-500 mb-3">{t.footerFollow}</p>
                <div className="flex space-x-3">
                  <a 
                    href="https://www.facebook.com/tsmchiro" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-zinc-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-all duration-300 group"
                    aria-label="Facebook 粉絲專頁"
                  >
                    <Facebook className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" />
                  </a>
                  <a 
                    href="https://www.instagram.com/tsmchiro" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-zinc-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 rounded-lg flex items-center justify-center transition-all duration-300 group"
                    aria-label="Instagram 帳號"
                  >
                    <Instagram className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" />
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4">{t.footerServices}</h3>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li>• {t.footerService1}</li>
                <li>• {t.footerService2}</li>
                <li>• {t.footerService3}</li>
                <li>• {t.footerService4}</li>
                <li>• {t.footerService5}</li>
                <li>• {t.footerService6}</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-zinc-800 pt-8">
            {/* SEO Keywords */}
            <div className="text-xs text-zinc-700 text-center mb-6 leading-relaxed">
              台南整復 | 佳里整復 | 美式整復 | 台南推拿 | 佳里推拿 | 運動按摩 | 深層按摩 | 筋膜放鬆 | 台南按摩 | 佳里按摩 | 久坐辦公族 | 久站工作族 | 體力工作族 | 規律運動族 | 運動後放鬆 | 日常身體調理 | 身體保養
            </div>

            <a 
              href="/booking/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block mb-8 px-8 py-4 bg-gradient-to-r from-amber-500 to-teal-500 text-zinc-950 font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 text-center text-lg"
            >
              {t.footerBookingBtn}
            </a>

            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-700 mb-8">
              <p className="text-zinc-400 text-sm leading-relaxed">
                <span className="font-semibold text-zinc-300">{t.footerDisclaimer}</span>{t.footerDisclaimerText}
              </p>
            </div>

            {/* Social Media Links */}
            <div className="flex justify-center space-x-6 mb-8">
              <a 
                href="https://www.facebook.com/tsmchiro" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center space-x-2 text-zinc-400 hover:text-blue-500 transition-colors"
              >
                <Facebook className="w-5 h-5" />
                <span className="text-sm">Facebook</span>
              </a>
              <a 
                href="https://www.instagram.com/tsmchiro" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center space-x-2 text-zinc-400 hover:text-pink-500 transition-colors"
              >
                <Instagram className="w-5 h-5" />
                <span className="text-sm">Instagram</span>
              </a>
              <a 
                href="https://lin.ee/ZCMyfyR" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center space-x-2 text-zinc-400 hover:text-teal-500 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm">LINE</span>
              </a>
            </div>
            
            <div className="text-center">
              <p className="text-zinc-500 text-sm mb-2">{t.footerCopyright}</p>
              <p className="text-zinc-600 text-xs">{t.footerTagline1}</p>
              <p className="text-zinc-600 text-xs mt-1">{t.footerTagline2}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}